import { _ } from "../../../polyglot/polyglot.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractImporter from "../AbstractImporter.js";
import AuthenticationStep from "../AuthenticationStep.js";
import EconomicCredential from "../../../../models/EconomicCredential.js";
import EconomicIntegration from "../../../../models/integration/EconomicIntegration.js";
import ImportOptionsSelectionStep from "./ImportOptionsSelectionStep.js";
import jQuery from "jquery";
import session from "../../../session.js";

/**
 * `EconomicImporter` Import wizard for the Economic integration.
 */
export default class EconomicImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		this._router = router.getRouter();
		this._credentials = new EconomicCredential();
		this._isNextButtonDisabled = true;
		this._submitted = false;
		this._partialImport = true;
		this._draftVoucherImport = false;

		const antiForgeryToken = session.getAntiForgeryToken();

		this._economicRepository = makeMonitorRepository({
			route: this._buildUrl({
				basePath: "/Shared/Economic/",
				relativeUrl: "ReturnUrl",
			}),
			antiForgeryToken,
		});

		this._budgetRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/Budget/",
			antiForgeryToken,
		});

		this._economicImportRepository = makeMonitorRepository({
			route: "/Shared/Import/Economic",
			antiForgeryToken,
		});

		this._companyChartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts",
			antiForgeryToken,
		});
	}

	routeName() {
		return "economic";
	}

	_buildUrl({ basePath, relativeUrl, filter }) {
		let url = basePath + relativeUrl;
		const filters = filter || {};
		const query = decodeURIComponent(jQuery.param(filters));
		url += query.length ? `?${query}` : "";
		return url;
	}

	_getAgencyId() {
		return (
			this._context.agency?.getId() ||
			this._context.company?.getAgencyId()
		);
	}

	_wizardSteps() {
		return [
			this._createAuthenticationStep(),
			this._createImportOptionsSelectionStep(),
		];
	}

	// Step 1: Launch the authentication flow.
	_createAuthenticationStep() {
		return new AuthenticationStep({
			submitCallback: this._launchAuthenticationFlow.bind(this),
		});
	}

	_createImportOptionsSelectionStep() {
		return new ImportOptionsSelectionStep({
			submitCallback: this._importOptionsToImportData.bind(this),
			redirectToEconomic: this._redirectToEconomic.bind(this),
		});
	}

	// Step 1 => 2 Authenticate the user if needed or proceed to
	// the next step.
	_launchAuthenticationFlow({ onDone }) {
		this._context.partialImport = true;
		this._context.importDraftVouchers = false;

		this._companyId = this._getCompanyId();

		this._agencyId = this._getAgencyId();

		this._context.economicToken =
			this._router.getParameter("economicToken") || null;

		this._context.economicAgreementNumber =
			this._router.getParameter("economicAgreementNumber") ??
			this._context.company.getBusinessIdentityCode();

		if (
			this._context.company.hasEconomicIntegrationWithAccessGrantToken()
		) {
			onDone();
		} else if (
			this._context.economicAgreementNumber &&
			this._context.economicToken
		) {
			onDone();
		} else {
			this._redirectToEconomic();
		}
	}

	// Redirects to Economic to finish the authentication step.
	_redirectToEconomic() {
		const companyId = this._context.company.getLegacyId();
		const returnUrl = window.location.href;
		const origin = window.location.origin;
		const appPublicToken = "Q0qLpiFwIC8p8Rf9CmmOGkxMey9vGdkTd2GMquBOm6w1";
		const authorizeEconomicReturnUrl = encodeURIComponent(
			`${origin}/Shared/Economic/Authorize/${companyId}`,
		);
		const economicUrl = `https://secure.e-conomic.com/secure/api1/requestaccess.aspx?appPublicToken=${appPublicToken}&redirecturl=${authorizeEconomicReturnUrl}`;
		window.location.href = economicUrl;

		this._updateEconomicReturnUrl({ companyId, returnUrl });
	}

	async _updateEconomicReturnUrl({ companyId, returnUrl }) {
		await this._economicRepository.update({
			model: {
				companyId,
				returnUrl,
			},
		});
	}

	// Step 2 => 3: Post the selected data loading options
	async _importOptionsToImportData({ onDone, step }) {
		const [importData, companyChartsOfAccounts, budgets] =
			await Promise.all([
				this._fetchEconomicData(),
				this._fetchChartsOfAccounts(),
				this._fetchBudgets(),
			]).catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});

		if (importData.hasErrors) {
			step.addSubmitError(this._errorToMessage(importData.typeOfError));
		}
		const allChartsOfAccounts = new Set([
			...(importData.chartOfAccounts || []),
			...companyChartsOfAccounts,
		]);

		const allBudgets = new Set([...(importData.budgets || []), ...budgets]);

		importData.chartOfAccounts = [...allChartsOfAccounts];
		importData.budgets = [...allBudgets];

		this._context.importData = importData;
		onDone();
	}

	_fetchEconomicData() {
		return this._economicImportRepository.save({
			filter: {
				companyId: this._companyId,
				agencyId: this._agencyId,
				partialImport: this._context.partialImport,
				draftVoucherImport: this._context.importDraftVouchers,
			},
			model: {
				agreementNumber: this._context.economicAgreementNumber,
				agreementGrantToken: this._context.economicToken,
			},
			notify: false,
		});
	}

	_fetchBudgets() {
		if (!this._companyId) {
			return Promise.resolve([]);
		}

		return this._budgetRepository.get({
			filter: { companyId: this._companyId },
		});
	}

	_fetchChartsOfAccounts() {
		if (!this._companyId) {
			return Promise.resolve([]);
		}

		return this._companyChartOfAccountsRepository.get({
			filter: { companyId: this._companyId },
		});
	}

	_errorToMessage(error) {
		switch (error) {
			case "Authentication": {
				return _(
					'Your credentials seems to have expired, please renew by clicking the button "Get new E-conomic credentials"',
				);
			}
			case "CompanyExpired": {
				return _("The company has expired and cannot be imported.");
			}
			case "NullAccountReference": {
				return _("Missing account reference");
			}
			default: {
				return error;
			}
		}
	}

	_selectOnlyLatestFiscalYear() {
		return true;
	}
}

EconomicImporter.integrationType = EconomicIntegration.getType();
